<template>
  <v-container v-if="order !== null" class="py-0">
    <v-card class="py-4 px-4">
      <booking-admin-multi-order
        :order="order"
        :comments="comments"
        :sessionSlot="sessionSlot"
        :selectedDay="selectedDay"
        @change-order="emitingOrderManipulation"
        @remove-order="emitingOrderManipulation"
        @pay-order="emitingOrderManipulation"
      ></booking-admin-multi-order>
    </v-card>
    <v-container class="pl-2 mt-16">
      <v-card-title>
        {{ $t("paymentsTitle") }}
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="currentOrder.payments"
        class="elevation-1"
        :no-data-text="$t('paymentsNoData')"
        hide-default-footer
      >
        <template v-slot:item.created="{ item }">
          <span>{{ item.created | dateFilter }}</span>
        </template>
        <template v-slot:item.payment_type="{ item }">
          <span>{{ translateFilter(item.payment_type) }}</span>
        </template>
      </v-data-table>
    </v-container>
  </v-container>
  <v-container v-else-if="loading">
    <v-card class="py-4 px-4">
      <v-skeleton-loader
        class="mx-n3 mt-n4"
        type="card-avatar, article, actions"
        tile
      >
      </v-skeleton-loader>
    </v-card>
  </v-container>
  <v-container v-else>
    <h2>{{ $t("orderNotFound") }}</h2>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment/moment";
import BookingAdminMultiOrder from "@/components/BookingAdminMultiOrder.vue";

export default {
  name: "OrderPage",
  filters: {
    dateFilter(value) {
      return moment(value).format("DD.MM.YYYY");
    },
  },
  components: {
    BookingAdminMultiOrder,
  },

  data() {
    return {
      orderId: null,
      locationId: null,
      orderTimeStart: null,
      playgroundId: null,
      order: null,
      orderFromSchedule: null,
      selectedDay: moment().format("YYYY-MM-DD"),
      sessionSlot: {
        time_start: null,
        playground: null,
        pricing: {
          currency: {
            blank_place: "",
            code: "",
            is_before: false,
          },
        },
        orders: [],
      },
      comments: null,
      loading: true,
      headers: [
        { text: "ID", value: "id", width: "5%" },
        {
          text: this.$i18n.t("paymentsType"),
          value: "payment_type",
          width: "10%",
        },
        {
          text: this.$i18n.t("paymentsCreated"),
          value: "created",
          width: "7%",
        },
        { text: this.$i18n.t("paymentsStatus"), value: "status", width: "10%" },
        {
          text: this.$i18n.t("paymentsExternal"),
          value: "external_id",
          width: "12%",
        },
        {
          text: this.$i18n.t("paymentsPurchase"),
          value: "purchase",
          width: "8%",
        },
        {
          text: this.$i18n.t("paymentsComment"),
          value: "comment",
          width: "15%",
        },
        { text: this.$i18n.t("paymentsUser"), value: "user", width: "15%" },
      ],
      payment_types: [
        {
          id: "1",
          title: this.$i18n.t("paymentsType1"),
        },
        {
          id: "2",
          title: this.$i18n.t("paymentsType2"),
        },
        {
          id: "3",
          title: this.$i18n.t("paymentsType3"),
        },
        {
          id: "4",
          title: this.$i18n.t("paymentsType4"),
        },
        {
          id: "5",
          title: this.$i18n.t("paymentsType5"),
        },
        {
          id: "6",
          title: this.$i18n.t("paymentsType6"),
        },
        {
          id: "7",
          title: this.$i18n.t("paymentsType7"),
        },
      ],
    };
  },
  async created() {
    try {
      await this.getCurrentOrder();
      await this.getCurrentOrderComment();
      await this.getCurrentLocation();
      await this.loadOrderFromSchedule();
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  },

  computed: {
    ...mapGetters(["location", "BASchedule", "currentOrder"]),
    formattedDate() {
      let itemBeginHours = moment
        .parseZone(this.sessionSlot.start)
        .format("HH");

      if (itemBeginHours >= 6 && itemBeginHours <= 23) {
        return moment.parseZone(this.selectedDay).format("DD.MM.YYYY");
      } else {
        return moment
          .parseZone(this.selectedDay)
          .add(1, "day")
          .format("DD.MM.YYYY");
      }
    },
  },

  methods: {
    ...mapActions([
      "loadBASchedule",
      "loadCurrentOrder",
      "loadBAOrderComments",
      "loadLocation",
    ]),
    async getCurrentOrder() {
      await this.loadCurrentOrder(this.$route.params.order_id);
      if (this.currentOrder) {
        this.orderId = this.currentOrder.order_id;
        this.orderTimeStart = this.currentOrder.session_time;
        this.convertDate(this.currentOrder.session_date);
        this.playgroundId = this.currentOrder.playsessions.find(
          (p) =>
            moment(p.session_dt).format("HH:mm") ===
            moment(this.orderTimeStart, "HH:mm:ss").format("HH:mm")
        ).playground_id;
        this.locationId = this.currentOrder.location_id;
      }
    },
    async loadOrderFromSchedule() {
      await this.loadBASchedule({
        playground_id: this.playgroundId,
        selected_date: this.selectedDay,
      });
      const orders = this.BASchedule.filter((el) => el.orders.length);
      this.orderFromSchedule = orders.find(
        (o) =>
          o.time_start ===
          moment(this.orderTimeStart, "HH:mm:ss").format("HH:mm")
      );
      if (this.orderFromSchedule) {
        this.order = this.orderFromSchedule.orders.find(
          (o) => o.id === this.orderId
        );
        this.sessionSlot = {
          start: this.orderFromSchedule.begin,
          time_start: moment
            .parseZone(this.orderFromSchedule.time_start, "HH:mm:ss")
            .format("HH:mm"),
          playground: this.order.playground_id,
          pricing: this.orderFromSchedule.pricing,
          orders: this.orderFromSchedule.orders,
          available_persons: this.orderFromSchedule.available_persons,
        };
      }
    },
    async getCurrentOrderComment() {
      const acc = {};
      const orderId = this.$route.params.order_id;
      const result = await this.loadBAOrderComments(orderId);
      acc[orderId] = result.data;
      this.comments = acc;
    },
    async getCurrentLocation() {
      await this.loadLocation(this.locationId);
    },
    async emitingOrderManipulation() {
      await this.getCurrentOrder();
      await this.getCurrentOrderComment();
      await this.getCurrentLocation();
      await this.loadOrderFromSchedule();
    },
    convertDate(date) {
      const itemBeginHours = moment
        .parseZone(this.orderTimeStart, "HH:mm:ss")
        .format("HH");

      if (itemBeginHours >= 6 && itemBeginHours <= 23) {
        return (this.selectedDay = date);
      } else {
        return (this.selectedDay = moment
          .parseZone(date)
          .add(-1, "day")
          .format("YYYY-MM-DD"));
      }
    },
    translateFilter(filter) {
      switch (filter) {
        case 1:
          return this.$i18n.t("paymentsType1");
        case 2:
          return this.$i18n.t("paymentsType2");
        case 3:
          return this.$i18n.t("paymentsType3");
        case 4:
          return this.$i18n.t("paymentsType4");
        case 5:
          return this.$i18n.t("paymentsType5");
        case 6:
          return this.$i18n.t("paymentsType6");
        case 7:
          return this.$i18n.t("paymentsType7");
      }
    },
  },
};
</script>
